import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, Loader2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { requestStepUpCode, verifyStepUpCode } from '../lib/api/auth';
import { persistAuthTokens } from '../lib/api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Listens for 'auth:step-up-required' events fired by the API client when a
 * refresh attempt is rejected because the device is unrecognized. Prompts the
 * user to enter a 6-digit code emailed to them, then promotes the device to
 * trusted on success.
 */
export default function StepUpModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // Email is sourced from (1) the auth context, (2) the step-up event detail
  // dispatched by the API client from the failed login payload, or (3) a
  // manual input as a final fallback. Without this, the "Email me a code"
  // button was disabled on login-time step-up because `user` is still null.
  const [emailOverride, setEmailOverride] = useState('');
  // "Trust this device for 30 days" — defaults ON because real daters check
  // their inbox once and then never want to be challenged again on the same
  // laptop/phone. Stays narrow (capped at 30d on the server) so a lost
  // device window is bounded.
  const [trustDevice, setTrustDevice] = useState(true);

  useEffect(() => {
    const handler = (e: Event) => {
      setOpen(true);
      setSent(false);
      setCode('');
      const detailEmail = (e as CustomEvent<{ email?: string }>).detail?.email;
      if (detailEmail) setEmailOverride(detailEmail);
    };
    window.addEventListener('auth:step-up-required', handler as EventListener);
    return () => window.removeEventListener('auth:step-up-required', handler as EventListener);
  }, []);

  if (!open) return null;
  const email = (user?.email || emailOverride || '').trim();
  const isValidEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  const sendCode = async () => {
    if (!isValidEmail) {
      toast.error('Enter your email first');
      return;
    }
    setBusy(true);
    try {
      await requestStepUpCode(email);
      setSent(true);
      toast.success('Code sent — check your email');
    } catch {
      toast.error('Could not send code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!isValidEmail || code.length < 4) return;
    setBusy(true);
    try {
      const res = await verifyStepUpCode(email, code.trim(), trustDevice ? 30 : 0);
      if (res.data.token) {
        persistAuthTokens(res.data);
      }
      toast.success(trustDevice ? 'Device trusted for 30 days' : 'Device verified');
      setOpen(false);
      // Reload to pick up the trusted session
      window.location.reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="step-up-modal">
        <motion.div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
              <ShieldCheck size={22} className="text-amber-600" />
            </div>
            <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-600" data-testid="step-up-close">
              <X size={18} />
            </button>
          </div>
          <h3 className="text-lg font-heading font-bold text-neutral-900 mb-1.5">Verify it's really you</h3>
          <p className="text-sm text-neutral-600 mb-5 leading-relaxed">
            We noticed a sign-in from a device we don't recognize. For your security, please verify with a one-time code we'll email to {email
              ? <span className="font-semibold">{email}</span>
              : <span className="font-semibold">your account</span>}.
          </p>

          {/* Manual email input — appears only when neither auth context nor
              the dispatch event supplied an email (e.g. a fresh tab hitting an
              expired refresh cookie). Lets the user always reach the code flow. */}
          {!user?.email && !sent && (
            <div className="mb-3">
              <label className="block text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-1">Your email</label>
              <input
                data-testid="step-up-email-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={emailOverride}
                onChange={(e) => setEmailOverride(e.target.value.trim().toLowerCase())}
                className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 focus:outline-none focus:border-dutch-teal focus:ring-2 focus:ring-dutch-teal/30 text-sm"
              />
            </div>
          )}

          {!sent ? (
            <Button
              data-testid="step-up-send-code"
              onClick={sendCode}
              disabled={busy || !isValidEmail}
              className="w-full bg-dutch-teal hover:bg-dutch-teal/90 text-white rounded-full gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {busy ? 'Sending…' : 'Email me a code'}
            </Button>
          ) : (
            <div className="space-y-3">
              <input
                data-testid="step-up-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]*"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono rounded-xl border border-neutral-200 focus:outline-none focus:border-dutch-teal focus:ring-2 focus:ring-dutch-teal/30"
                autoFocus
              />
              {/* Trust-this-device opt-out. Defaults ON because most users
                  return to the same browser repeatedly; the 30-day cap
                  keeps the security window narrow. */}
              <label
                htmlFor="step-up-trust-device"
                className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-neutral-50 border border-neutral-200 cursor-pointer hover:bg-neutral-100 transition-colors"
              >
                <input
                  id="step-up-trust-device"
                  data-testid="step-up-trust-device"
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-dutch-teal focus:ring-dutch-teal/40 cursor-pointer"
                />
                <span className="text-xs text-neutral-700 leading-snug">
                  <span className="font-semibold text-neutral-900">Trust this device for 30 days.</span>{' '}
                  We'll skip this check on this browser unless something looks risky.
                </span>
              </label>
              <Button
                data-testid="step-up-verify"
                onClick={verify}
                disabled={busy || code.length < 6}
                className="w-full bg-dutch-teal hover:bg-dutch-teal/90 text-white rounded-full disabled:opacity-50 gap-2"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {busy ? 'Verifying…' : 'Verify code'}
              </Button>
              <button
                data-testid="step-up-resend"
                onClick={sendCode}
                disabled={busy}
                className="w-full text-xs text-dutch-teal hover:underline"
              >
                Didn't get it? Send again
              </button>
            </div>
          )}
          <p className="text-[10px] text-neutral-400 text-center mt-5">
            If you didn't try to sign in, change your password and email <a href="mailto:hello@dutchdate.app?subject=Security%20Report" className="underline">hello@dutchdate.app</a>.
          </p>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
